import { useMemo, useState } from 'react'
import {
  Alert,
  Badge,
  Group,
  Skeleton,
  Stack,
  Text,
  Button,
} from '@mantine/core'
import { AreaChart } from '@mantine/charts'
import { useQuery } from '@tanstack/react-query'
import { IconCalendar, IconChartLine, IconInfoCircle, IconRefresh, IconSettings } from '@tabler/icons-react'
import { useFinopsData } from '@/hooks/useFinopsData'
import { useCan } from '@/store/auth'
import { finopsApi } from '@/api/finops'
import { useTenantBudgetStatus } from '@/hooks/useTenantBudget'
import { WidgetCard, EmptyState } from '@/components/ui'
import { formatUSD } from '@/utils/finops'
import { BudgetConfigureModal } from '@/components/finops/BudgetConfigureModal'
import type { FinopsForecastConfidence } from '@/types/finops'

const CONFIDENCE_COLORS: Record<FinopsForecastConfidence, string> = {
  low: 'red',
  medium: 'yellow',
  high: 'green',
}

const CONFIDENCE_LABELS: Record<FinopsForecastConfidence, string> = {
  low: 'Baixa',
  medium: 'Média',
  high: 'Alta',
}

export function ForecastTab() {
  const { tenantId } = useFinopsData()
  const can = useCan()
  const [budgetModalOpen, setBudgetModalOpen] = useState(false)

  const canConfigureBudget = can('finops', 'configure_budget')

  // SP4 (§5.7 / §9): `getCurrentForecast()` chama GET /v1/finops/forecast/current
  // SEM tenantId. No backend, o ramo source='report' IGNORA o scope do caller
  // (api_routes.py forecast_current), logo o forecast pode vazar custo org-wide
  // para não-admin. Em vez de migrar o gráfico para o /trend escopado (SP3) — que
  // exigiria reescrever a fonte de dados e os display_hints — gateamos a aba como
  // admin-only (admin:platform). É a opção mais simples que fecha o vazamento de
  // UX descrito na spec; a migração para /trend fica como follow-up.
  const isPlatformAdmin = can('admin', 'platform')

  const forecastQuery = useQuery({
    queryKey: ['finops-forecast-current'],
    queryFn: () => finopsApi.getCurrentForecast(),
    enabled: isPlatformAdmin,
    staleTime: 300_000,
  })

  const budgetQuery = useTenantBudgetStatus(tenantId!, { enabled: tenantId != null })

  const forecastData = forecastQuery.data ?? null
  const forecast = forecastData?.forecast ?? null
  const budget = budgetQuery.data ?? null

  const chartData = useMemo(() => {
    if (!forecast) return []
    return forecast.weekly_projection.map((p) => ({
      week: p.week,
      projection: p.projected_usd,
      ci_lower: p.ci_lower,
      ci_upper: p.ci_upper,
    }))
  }, [forecast])

  const overrunAlert = useMemo(() => {
    if (!forecast || !budget?.threshold_usd) return null
    const threshold = budget.alert_at_pct ?? 80
    const cap = budget.threshold_usd * (threshold / 100)
    if (forecast.monthly_projection_usd <= cap) return null
    return { cap, threshold, projected: forecast.monthly_projection_usd, budget: budget.threshold_usd }
  }, [forecast, budget])

  if (!isPlatformAdmin) {
    return (
      <EmptyState
        icon={IconChartLine}
        title="Forecast disponível apenas para administradores"
        description="O forecast atual é calculado em escopo de plataforma. Para projeção escopada ao seu tenant, use a visão de Tendência (Dashboard)."
        accent="info"
      />
    )
  }

  if (forecastQuery.isLoading) {
    return <Skeleton height={300} />
  }

  if (!forecast) {
    return (
      <EmptyState
        icon={IconChartLine}
        title="Sem histórico suficiente"
        description="Precisamos de ao menos 2 semanas de coleta para projetar custos. Volte na próxima segunda."
        accent="info"
      />
    )
  }

  return (
    <Stack>
      <Group justify="space-between">
        <Group gap="xs">
          <Text size="sm" fw={600}>Forecast 30 dias:</Text>
          <Text size="sm" c="teal" fw={700}>{formatUSD(forecast.monthly_projection_usd)}</Text>
          <Badge variant="light" color={CONFIDENCE_COLORS[forecast.confidence]}>
            Confiança {CONFIDENCE_LABELS[forecast.confidence]}
          </Badge>
        </Group>
        <Group gap="xs">
          {canConfigureBudget && tenantId && (
            <Button
              size="xs"
              variant="light"
              leftSection={<IconSettings size={14} />}
              onClick={() => setBudgetModalOpen(true)}
            >
              Configurar Budget
            </Button>
          )}
        </Group>
      </Group>

      {/* Banner de fonte: relatório semanal ou projeção ao vivo */}
      <Alert
        variant="light"
        color={forecastData?.source === 'live_fallback' ? 'blue' : 'gray'}
        icon={forecastData?.source === 'live_fallback' ? <IconRefresh size={16} /> : <IconCalendar size={16} />}
      >
        {forecastData?.source === 'live_fallback'
          ? 'Projeção ao vivo — nenhum relatório semanal com forecast disponível. Calculado em tempo real.'
          : forecastData?.generated_at
            ? `Relatório semanal de ${new Date(forecastData.generated_at).toLocaleDateString('pt-BR')}`
            : 'Relatório semanal mais recente'}
      </Alert>

      {forecast.display_hints.warning_banner && (
        <Alert
          variant="light"
          color={forecast.display_hints.warning_banner === 'low_confidence' ? 'red' : 'yellow'}
          icon={<IconInfoCircle size={16} />}
        >
          {forecast.display_hints.warning_message ??
            (forecast.display_hints.warning_banner === 'low_confidence'
              ? 'Confiança baixa: poucos pontos de dados. Resultados podem variar significativamente.'
              : 'Confiança média: tendência detectada mas variabilidade elevada.')}
        </Alert>
      )}

      {overrunAlert && (
        <Alert variant="light" color="red" icon={<IconInfoCircle size={16} />}>
          Forecast 30 dias <strong>{formatUSD(overrunAlert.projected)}</strong> excede{' '}
          {overrunAlert.threshold}% do budget configurado (
          {formatUSD(overrunAlert.budget)}). Limite de alerta: {formatUSD(overrunAlert.cap)}.
        </Alert>
      )}

      <WidgetCard p="md">
        <Text size="sm" fw={600} mb="sm">
          Projeção próximas {forecast.weekly_projection.length} semanas
        </Text>
        <AreaChart
          h={300}
          data={chartData}
          dataKey="week"
          series={[
            { name: 'ci_upper', label: 'IC Superior', color: 'teal.3' },
            { name: 'projection', label: 'Projeção', color: 'teal.6' },
            { name: 'ci_lower', label: 'IC Inferior', color: 'teal.2' },
          ]}
          valueFormatter={(v) => formatUSD(v)}
          curveType="monotone"
          withLegend
        />
      </WidgetCard>

      <WidgetCard p="md">
        <Text size="sm" fw={600} mb="sm">Detalhes da projeção</Text>
        <Stack gap="xs">
          <Group justify="space-between">
            <Text size="sm" c="dimmed">Método</Text>
            <Text size="sm">{forecast.method}</Text>
          </Group>
          <Group justify="space-between">
            <Text size="sm" c="dimmed">Confiança</Text>
            <Badge variant="light" color={CONFIDENCE_COLORS[forecast.confidence]}>
              {CONFIDENCE_LABELS[forecast.confidence]}
            </Badge>
          </Group>
          <Group justify="space-between">
            <Text size="sm" c="dimmed">Pontos de dados</Text>
            <Text size="sm">{forecast.n_data_points}</Text>
          </Group>
          <Group justify="space-between">
            <Text size="sm" c="dimmed">Slope (variação semanal)</Text>
            <Text size="sm">{formatUSD(forecast.slope_usd_per_week)}/semana</Text>
          </Group>
          <Group justify="space-between">
            <Text size="sm" c="dimmed">Projeção mensal</Text>
            <Text size="sm" fw={600}>{formatUSD(forecast.monthly_projection_usd)}</Text>
          </Group>
          {budget?.threshold_usd != null && (
            <Group justify="space-between">
              <Text size="sm" c="dimmed">Budget atual</Text>
              <Text size="sm" fw={600}>{formatUSD(budget.threshold_usd)}</Text>
            </Group>
          )}
        </Stack>
      </WidgetCard>

      {tenantId && (
        <BudgetConfigureModal
          opened={budgetModalOpen}
          onClose={() => setBudgetModalOpen(false)}
          tenantId={tenantId}
          initialBudget={budget?.threshold_usd ?? null}
          initialThreshold={budget?.alert_at_pct ?? null}
        />
      )}
    </Stack>
  )
}
